'use client';
import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Trackable } from '@/components/common';
import { useContactForm } from '@/hooks';
import { Toaster } from 'react-hot-toast';
import { cn } from '@/lib';
import { Loader2 } from 'lucide-react';

const validationSchema = Yup.object({
  name: Yup.string().required('Full Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
    .required('Mobile number is required'),
  message: Yup.string().required('Message is required'),
});

export default function ContactForm() {
  const { handleSubmit, submitted} = useContactForm();

  return (
    <div className='mt-4 w-full max-w-4xl rounded-2xl px-4 sm:mt-0 md:px-0'>
      <h1 className='mb-2 text-2xl font-semibold sm:text-3xl'>Contact Us</h1>
      <p className='mb-8 font-medium text-gray-600'>
        Get in touch with us using the enquiry form or contact details below.
      </p>
      {submitted ? (
        <div className='text-center font-medium text-green-600'>
          Thank you for contacting us! We&apos;ll get back to you soon.
        </div>
      ) : (
        <Formik
          initialValues={{
            name: '',
            email: '',
            mobile: '',
            message: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            await handleSubmit({
              values,
              resetForm,
            });
          }}
        >
          {({ isSubmitting }) => (
            <Form className='space-y-5'>
              <div>
                <label
                  className='mb-1 block text-sm font-medium'
                  htmlFor='name'
                >
                  Full Name
                </label>
                <Field
                  id='name'
                  name='name'
                  placeholder='Enter your full name'
                  type='text'
                  required
                  className='focus:ring-primary w-full rounded-lg border px-4 py-2 focus:ring-2 focus:outline-none'
                />
                <ErrorMessage
                  name='name'
                  component='div'
                  className='mt-1 text-sm text-red-600'
                />
              </div>

              <div>
                <label
                  className='mb-1 block text-sm font-medium'
                  htmlFor='email'
                >
                  Email
                </label>
                <Field
                  id='email'
                  name='email'
                  type='email'
                  placeholder='Enter your email'
                  required
                  className='focus:ring-primary w-full rounded-lg border px-4 py-2 focus:ring-2 focus:outline-none'
                />
                <ErrorMessage
                  name='email'
                  component='div'
                  className='mt-1 text-sm text-red-600'
                />
              </div>

              <div>
                <label
                  className='mb-1 block text-sm font-medium'
                  htmlFor='mobile'
                >
                  Mobile Number
                </label>
                <Field
                  id='mobile'
                  name='mobile'
                  placeholder='Enter your mobile number'
                  type='tel'
                  required
                  pattern='[0-9]{10}'
                  className='focus:ring-primary w-full rounded-lg border px-4 py-2 focus:ring-2 focus:outline-none'
                />
                <ErrorMessage
                  name='mobile'
                  component='div'
                  className='mt-1 text-sm text-red-600'
                />
              </div>

              <div>
                <label
                  className='mb-1 block text-sm font-medium'
                  htmlFor='message'
                >
                  What brought you here today?
                </label>
                <Field
                  as='textarea'
                  id='message'
                  name='message'
                  placeholder='Share the details so we can help you out...'
                  rows={4}
                  required
                  className='focus:ring-primary w-full rounded-lg border px-4 py-2 focus:ring-2 focus:outline-none'
                />
                <ErrorMessage
                  name='message'
                  component='div'
                  className='mt-1 text-sm text-red-600'
                />
              </div>
              <Trackable eventName='Submit' eventScope='Contact Form'>
                <button
                  type='submit'
                  disabled={isSubmitting}
                  className={cn(
                    'bg-primary w-full rounded-lg py-2 font-semibold text-white transition',
                    {
                      'bg-zinc-300': isSubmitting,
                    },
                  )}
                >
                  {isSubmitting ? (
                    <Loader2 className='mx-auto animate-spin' />
                  ) : (
                    'Submit'
                  )}
                </button>
              </Trackable>
            </Form>
          )}
        </Formik>
      )}
      <Toaster position='top-center' />
    </div>
  );
}
